/**
 * Hoja "Cargar créditos" (speak Fase 23, monetización (b)). Lista los packs de
 * crédito (de `/api/iap/packs`) y, en NATIVO con RevenueCat configurado, dispara
 * la compra por Google Play Billing → al concretarse, llama `/api/iap/sync` para
 * que el backend acredite (idempotente) y refresca el saldo de la Home.
 *
 * Tres estados de disponibilidad:
 *   - Nativo + RevenueCat configurado → compra real.
 *   - Nativo sin key (p. ej. iOS 1.0, sin IAP todavía) → la app no ofrece recargar
 *     (`isRechargeOffered`): nada abre esta hoja, y si algo la abriera igual no
 *     lista packs ni promete un "próximamente" (App Review 2.1 / 2.2).
 *   - Web → la recarga vive en la app (RevenueCat es nativo; no hay checkout web).
 *
 * El pie nombra a quien procesa el pago según la plataforma (`paymentProcessorNote`):
 * en iOS nunca se menciona Google Play (App Review 2.3.10).
 *
 * El refresh del saldo se propaga por el evento global `orborix:credits.refresh`
 * (lo escucha `useCredits`), para no threadear callbacks por todo el wallet.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { formatOrbs } from '@orborix/shared-types';
import { useAuth } from '../auth/AuthProvider.js';
import { isNative, nativePlatform } from '../lib/platform.js';
import { holdOtaApply } from '../lib/native/ota.js';
import { fetchCreditPacks, syncIapPurchases, type CreditPackDto } from './iapApi.js';
import {
  fetchLocalizedPrices,
  isPurchaseAvailable,
  purchaseCreditPack,
  type PurchaseOutcome,
} from './revenuecat.js';
import './wallet.css';
import { useSheetDrag } from '../hooks/useSheetDrag.js';
import { track } from '../lib/analytics.js';

function IconClose({ size = 18 }: { size?: number }): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 6 L 18 18 M18 6 L 6 18" />
    </svg>
  );
}

/**
 * Who processes the payment, per platform: the store of the device. `null` on
 * the web, where nothing is sold (the sheet points to the mobile app).
 */
export function paymentProcessorNote(platform: 'ios' | 'android' | 'web'): string | null {
  switch (platform) {
    case 'ios':
      return 'El pago lo procesa Apple.';
    case 'android':
      return 'El pago lo procesa Google Play.';
    case 'web':
      return null;
  }
}

interface AddCreditsSheetProps {
  onClose: () => void;
}

type Phase = { kind: 'idle' } | { kind: 'buying'; productId: string } | { kind: 'done'; creditedCount: number };

export function AddCreditsSheet({ onClose }: AddCreditsSheetProps): ReactElement {
  const { grabProps, sheetStyle } = useSheetDrag(onClose);
  const { user } = useAuth();
  const [packs, setPacks] = useState<CreditPackDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [prices, setPrices] = useState<Map<string, string>>(new Map());
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [buyError, setBuyError] = useState<string | null>(null);

  const purchasable = isPurchaseAvailable();
  // Native without the store key: no packs, no disabled "Comprar" (see header).
  const listPacks = purchasable || !isNative;
  const processorNote = paymentProcessorNote(nativePlatform());

  // Product analytics (lib/analytics.ts): the sheet opened, and whether it can sell here.
  useEffect(() => {
    track('purchase_sheet_opened', { purchasable });
  }, [purchasable]);

  // Scroll lock del body mientras la hoja está abierta.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Cargar packs (catálogo del backend) + precios localizados (best-effort).
  useEffect(() => {
    if (!listPacks) return;
    const ctrl = new AbortController();
    let alive = true;
    void (async () => {
      try {
        const list = await fetchCreditPacks(ctrl.signal);
        if (!alive) return;
        setPacks(list);
        if (purchasable && user) {
          const localized = await fetchLocalizedPrices(
            user.id,
            list.map((p) => p.productId),
          );
          if (alive) setPrices(localized);
        }
      } catch (err) {
        if (alive && !(err instanceof DOMException && err.name === 'AbortError')) {
          setLoadError('No pudimos cargar los packs. Probá de nuevo.');
        }
      }
    })();
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [listPacks, purchasable, user]);

  async function onBuy(pack: CreditPackDto): Promise<void> {
    if (!user) return;
    // Native OTA (lib/native/ota.ts): no bundle update from the request until the
    // purchase settles — on Android the store sheet sends the app to the background,
    // which is exactly when the updater applies a staged bundle.
    const releaseOta = holdOtaApply('iap-purchase');
    try {
      await buyPack(pack, user.id);
    } finally {
      releaseOta();
    }
  }

  async function buyPack(pack: CreditPackDto, userId: string): Promise<void> {
    setBuyError(null);
    setPhase({ kind: 'buying', productId: pack.productId });

    // Paso 1 — compra en la store. Un fallo ACÁ significa que NO se cobró nada.
    let outcome: PurchaseOutcome;
    try {
      outcome = await purchaseCreditPack(pack.productId, userId);
    } catch {
      setBuyError('No se pudo iniciar la compra. No se te cobró nada.');
      setPhase({ kind: 'idle' });
      return;
    }
    if (outcome === 'cancelled') {
      setPhase({ kind: 'idle' });
      return;
    }

    track('purchase_completed', { product_id: pack.productId });

    // Paso 2 — la compra se CONCRETÓ (RevenueCat la validó): a partir de acá el
    // crédito está GARANTIZADO (si el sync falla, el webhook lo acredita igual).
    // Pase lo que pase, refrescamos el saldo y mostramos éxito — nunca un error
    // que sugiera que la compra falló (ya se cobró y se va a acreditar).
    let creditedCount = 0;
    try {
      const result = await syncIapPurchases();
      creditedCount = result.creditedCount;
    } catch {
      // Sync falló pero la compra es válida → el webhook acreditará. No es error
      // para el usuario; el saldo se actualizará en breve.
    }
    window.dispatchEvent(new Event('orborix:credits.refresh'));
    setPhase({ kind: 'done', creditedCount });
  }

  return (
    <div className="bal-overlay" role="dialog" aria-modal="true" aria-label="Cargar créditos" onClick={onClose}>
      <div className="bal-sheet" style={sheetStyle} onClick={(e) => e.stopPropagation()}>
        <div className="bal-handle sheet-grab" {...grabProps} />
        <div className="bal-sheet-head">
          <span className="tag" style={{ color: 'var(--text-muted)' }}>
            CARGAR ORBS
          </span>
          <button type="button" className="bal-close" onClick={onClose} aria-label="Cerrar">
            <IconClose />
          </button>
        </div>

        {phase.kind === 'done' ? (
          <div className="addc-done">
            <div className="addc-done-check" aria-hidden="true">✓</div>
            <p className="addc-done-title">¡Listo!</p>
            <p className="addc-done-sub">
              {phase.creditedCount > 0
                ? 'Tus orbs ya están en tu saldo.'
                : 'Si la compra se concretó, tu saldo se actualiza en un momento.'}
            </p>
            <button type="button" className="bal-addbtn addc-done-btn" onClick={onClose}>
              Volver
            </button>
          </div>
        ) : (
          <>
            {!listPacks && (
              <p className="addc-note">La recarga no está disponible en este dispositivo.</p>
            )}
            {listPacks && !purchasable && (
              <p className="addc-note">
                La recarga está disponible en la app de Orborix Speak para móvil.
              </p>
            )}

            {listPacks && loadError && <div className="bal-empty">{loadError}</div>}
            {listPacks && !loadError && packs === null && (
              <div className="bal-empty">cargando packs…</div>
            )}

            {listPacks && packs?.map((pack) => {
              const buying = phase.kind === 'buying';
              const thisBuying = phase.kind === 'buying' && phase.productId === pack.productId;
              // Precio LOCALIZADO real de la store (lo que pagás, moneda local).
              // Si no hay (web / sin RevenueCat) NO mostramos un fallback en USD
              // — el user nunca ve dólares dentro de la app; ahí la compra está
              // deshabilitada igual.
              const priceLabel = prices.get(pack.productId);
              return (
                <div key={pack.productId} className="addc-row">
                  <div className="addc-row-body">
                    <span className="addc-row-amount">{formatOrbs(pack.creditsNano)} orbs</span>
                    <span className="addc-row-sub">para practicar</span>
                  </div>
                  <button
                    type="button"
                    className="addc-buy"
                    disabled={!purchasable || buying}
                    onClick={() => void onBuy(pack)}
                  >
                    {thisBuying ? 'Procesando…' : priceLabel ? `Comprar · ${priceLabel}` : 'Comprar'}
                  </button>
                </div>
              );
            })}

            {buyError && <div className="addc-error">{buyError}</div>}

            <p className="addc-foot">
              {processorNote ? `${processorNote} ` : ''}Los orbs se usan para conversación,
              correcciones y la Audioteca.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
